import React, { memo, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  ArrowForwardRounded,
  AutoAwesomeRounded,
  CodeRounded,
  HubRounded,
  PsychologyRounded,
  RocketLaunchRounded,
  CenterFocusStrongRounded,
  OpenWithRounded,
  ZoomInRounded,
  ZoomOutRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type ZoneId = 'profile' | 'stack' | 'ai' | 'missions';

interface MapView {
  scale: number;
  x: number;
  y: number;
}

const DEFAULT_MAP_VIEW: MapView = { scale: 1, x: 0, y: 0 };

const ZONE_MAP_VIEWS: Record<ZoneId, MapView> = {
  profile: { scale: 1.12, x: 0, y: 8 },
  stack: { scale: 1.2, x: 105, y: 10 },
  ai: { scale: 1.2, x: -100, y: 62 },
  missions: { scale: 1.18, x: -95, y: -58 },
};

interface Zone {
  id: ZoneId;
  code: string;
  title: string;
  eyebrow: string;
  description: string;
  meta: string;
  target: string;
  icon: React.ReactNode;
}

const GameWorld: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isPt = i18n.language.startsWith('pt');
  const [activeZone, setActiveZone] = useState<ZoneId>('profile');
  const [mapView, setMapView] = useState<MapView>(DEFAULT_MAP_VIEW);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(null);

  const zones = useMemo<Zone[]>(() => [
    {
      id: 'profile',
      code: '01',
      eyebrow: isPt ? 'PERFIL' : 'PROFILE',
      title: isPt ? 'Desenvolvedor Full Stack' : 'Full Stack Developer',
      description: isPt
        ? 'Desenvolvedor de Software Sênior e Tech Lead construindo produtos escaláveis, APIs e sistemas distribuídos.'
        : 'Senior Software Developer and Tech Lead building scalable products, APIs, and distributed systems.',
      meta: isPt ? '7+ anos em produção' : '7+ years in production',
      target: 'about',
      icon: <CodeRounded />,
    },
    {
      id: 'stack',
      code: '02',
      eyebrow: isPt ? 'TECNOLOGIAS' : 'TECHNOLOGIES',
      title: isPt ? 'Stack & Arquitetura' : 'Stack & Architecture',
      description: isPt
        ? 'Node.js, TypeScript, NestJS, React, Go, Python, cloud, observabilidade e arquitetura moderna.'
        : 'Node.js, TypeScript, NestJS, React, Go, Python, cloud, observability, and modern architecture.',
      meta: isPt ? '30+ tecnologias' : '30+ technologies',
      target: 'skills',
      icon: <HubRounded />,
    },
    {
      id: 'ai',
      code: '03',
      eyebrow: isPt ? 'INTELIGÊNCIA ARTIFICIAL' : 'ARTIFICIAL INTELLIGENCE',
      title: isPt ? 'IA aplicada à engenharia' : 'AI for engineering',
      description: isPt
        ? 'Agentes, Codex, Claude Code, prompt engineering e o ai-kit transformando contexto em fluxos reutilizáveis.'
        : 'Agents, Codex, Claude Code, prompt engineering, and ai-kit turning context into reusable workflows.',
      meta: isPt ? 'Agentes, workflows e tooling' : 'Agents, workflows, and tooling',
      target: 'skills',
      icon: <PsychologyRounded />,
    },
    {
      id: 'missions',
      code: '04',
      eyebrow: isPt ? 'PROJETOS' : 'PROJECTS',
      title: isPt ? 'Projetos selecionados' : 'Selected projects',
      description: isPt
        ? 'Ferramentas para devs, APIs, aplicações web, algoritmos e experimentos de inteligência artificial.'
        : 'Developer tooling, APIs, web apps, algorithms, and artificial intelligence experiments.',
      meta: isPt ? '15+ projetos entregues' : '15+ projects delivered',
      target: 'projects',
      icon: <RocketLaunchRounded />,
    },
  ], [isPt]);

  const selectedZone = zones.find((zone) => zone.id === activeZone) ?? zones[0];

  const selectZone = (zoneId: ZoneId) => {
    setActiveZone(zoneId);
    setMapView(ZONE_MAP_VIEWS[zoneId]);
  };

  const zoomMap = (amount: number) => {
    setMapView((current) => ({
      ...current,
      scale: Math.min(1.45, Math.max(0.85, Number((current.scale + amount).toFixed(2)))),
    }));
  };

  const resetMap = () => setMapView(DEFAULT_MAP_VIEW);

  const handleMapPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as Element).closest('button, [role="button"]')) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: mapView.x,
      originY: mapView.y,
    };
    setIsDragging(true);
  };

  const handleMapPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    setMapView((current) => ({
      ...current,
      x: Math.max(-190, Math.min(190, drag.originX + event.clientX - drag.startX)),
      y: Math.max(-130, Math.min(130, drag.originY + event.clientY - drag.startY)),
    }));
  };

  const handleMapPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragState.current?.pointerId === event.pointerId) {
      dragState.current = null;
      setIsDragging(false);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  };

  const handleZoneKeyDown = (event: React.KeyboardEvent<SVGGElement>, zoneId: ZoneId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectZone(zoneId);
    }
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="about"
      className={`game-world ${theme.palette.mode === 'dark' ? 'game-world--dark' : ''}`}
      aria-label={isPt ? 'Mapa interativo do portfólio' : 'Interactive portfolio map'}
    >
      <div className="game-world__ambient game-world__ambient--one" />
      <div className="game-world__ambient game-world__ambient--two" />

      <div className="game-world__shell">
        <div className="world-metrics" aria-label={isPt ? 'Resumo do perfil' : 'Profile summary'}>
          <div className="world-metric world-metric--identity">
            <div className="world-avatar" aria-hidden="true">AC</div>
            <div>
              <span>{isPt ? 'PORTFÓLIO' : 'PORTFOLIO'}</span>
              <strong>Ângelo Castro</strong>
            </div>
          </div>
          <div className="world-metric">
            <span>{isPt ? 'EXPERIÊNCIA' : 'EXPERIENCE'}</span>
            <strong>07+</strong>
            <small>{isPt ? 'anos de experiência' : 'years of experience'}</small>
          </div>
          <div className="world-metric">
            <span>{isPt ? 'PROJETOS' : 'PROJECTS'}</span>
            <strong>15+</strong>
            <small>{isPt ? 'projetos entregues' : 'projects delivered'}</small>
          </div>
          <div className="world-metric">
            <span>{isPt ? 'TECNOLOGIAS' : 'TECHNOLOGIES'}</span>
            <strong>30+</strong>
            <small>{isPt ? 'tecnologias na stack' : 'technologies in stack'}</small>
          </div>
        </div>

        <div className="world-dashboard">
          <aside className="world-intro-panel game-panel">
            <div className="game-panel__eyebrow">
              {isPt ? 'APRESENTAÇÃO PROFISSIONAL' : 'PROFESSIONAL PROFILE'}
            </div>
            <h1>Ângelo Castro</h1>
            <div className="world-intro-panel__role">{t('hero.role')}</div>
            <p>{t('hero.description')}</p>
            <button className="game-primary-button" onClick={() => scrollTo('projects')}>
              <RocketLaunchRounded fontSize="small" />
              {isPt ? 'Ver projetos' : 'View projects'}
            </button>
            <button className="game-text-button" onClick={() => scrollTo('skills')}>
              {isPt ? 'Explorar tecnologias' : 'Explore technologies'}
              <ArrowForwardRounded fontSize="small" />
            </button>
          </aside>

          <div className="world-map-wrap">
            <div className="world-map-label">
              <span>{isPt ? 'ECOSSISTEMA DE SOFTWARE' : 'SOFTWARE ECOSYSTEM'}</span>
              <small>{isPt ? 'Explore os módulos e o fluxo da arquitetura' : 'Explore the architecture modules and flow'}</small>
            </div>

            <div className="world-map-controls" aria-label={isPt ? 'Controles do mapa' : 'Map controls'}>
              <button type="button" onClick={() => zoomMap(-0.1)} aria-label={isPt ? 'Diminuir zoom' : 'Zoom out'} title={isPt ? 'Diminuir zoom' : 'Zoom out'}>
                <ZoomOutRounded fontSize="small" />
              </button>
              <button type="button" onClick={resetMap} aria-label={isPt ? 'Recentralizar mapa' : 'Reset map'} title={isPt ? 'Recentralizar' : 'Reset'}>
                <CenterFocusStrongRounded fontSize="small" />
              </button>
              <button type="button" onClick={() => zoomMap(0.1)} aria-label={isPt ? 'Aumentar zoom' : 'Zoom in'} title={isPt ? 'Aumentar zoom' : 'Zoom in'}>
                <ZoomInRounded fontSize="small" />
              </button>
            </div>

            <div className="world-map-instruction" aria-hidden="true">
              <OpenWithRounded fontSize="small" />
              {isPt ? 'Arraste para explorar' : 'Drag to explore'}
            </div>

            <div
              className={`world-map-canvas ${isDragging ? 'is-dragging' : ''}`}
              style={{ transform: `translate3d(${mapView.x}px, ${mapView.y}px, 0) scale(${mapView.scale})` }}
              onPointerDown={handleMapPointerDown}
              onPointerMove={handleMapPointerMove}
              onPointerUp={handleMapPointerUp}
              onPointerCancel={handleMapPointerUp}
            >
              <svg className="world-map" viewBox="0 0 900 560" role="img" aria-label={isPt ? 'Diagrama interativo de um ecossistema de software' : 'Interactive software ecosystem diagram'}>
                <defs>
                  <linearGradient id="world-board" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="var(--world-board-start)" />
                    <stop offset="100%" stopColor="var(--world-board-end)" />
                  </linearGradient>
                  <linearGradient id="world-core-gradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#347ff5" />
                    <stop offset="100%" stopColor="#7659e4" />
                  </linearGradient>
                  <linearGradient id="world-ai-gradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#9a78ff" />
                    <stop offset="100%" stopColor="#6541d5" />
                  </linearGradient>
                  <linearGradient id="world-project-gradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#ffb64d" />
                    <stop offset="100%" stopColor="#ef7d2d" />
                  </linearGradient>
                  <pattern id="world-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                    <path d="M32 0H0V32" fill="none" stroke="var(--world-grid-line)" strokeWidth="1" />
                  </pattern>
                  <filter id="world-shadow" x="-40%" y="-40%" width="180%" height="200%">
                    <feDropShadow dx="0" dy="16" stdDeviation="18" floodColor="#071d3a" floodOpacity=".28" />
                  </filter>
                  <filter id="world-glow" x="-100%" y="-100%" width="300%" height="300%">
                    <feGaussianBlur stdDeviation="7" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <rect x="42" y="66" width="816" height="424" rx="48" fill="url(#world-board)" stroke="var(--world-board-border)" strokeWidth="2" filter="url(#world-shadow)" />
                <rect x="42" y="66" width="816" height="424" rx="48" fill="url(#world-grid)" opacity=".7" />
                <path d="M83 118h126M691 438h126" stroke="var(--world-circuit-muted)" strokeWidth="2" strokeDasharray="4 8" />
                <circle cx="82" cy="96" r="5" fill="#ff6b7a" />
                <circle cx="100" cy="96" r="5" fill="#ffbd59" />
                <circle cx="118" cy="96" r="5" fill="#50dea4" />
                <text x="140" y="101" className="world-svg-kicker">distributed.systems / portfolio</text>
                <text x="817" y="101" textAnchor="end" className="world-svg-status">
                  {isPt ? 'ARQUITETURA INTERATIVA' : 'INTERACTIVE ARCHITECTURE'}
                </text>

                <path className="world-circuit-line" d="M340 278 C314 278 307 278 292 278" />
                <path className="world-circuit-line world-circuit-line--purple" d="M542 230 C576 196 594 176 626 162" />
                <path className="world-circuit-line world-circuit-line--gold" d="M542 337 C580 365 598 383 632 402" />
                <path className="world-circuit-line world-circuit-line--muted" d="M386 190 C360 155 318 142 268 139" />
                <path className="world-circuit-line world-circuit-line--muted" d="M367 375 C327 405 286 422 244 432" />
                <path className="world-circuit-line world-circuit-line--muted" d="M494 376 C500 417 470 438 448 452" />

                <g className="world-orbit-chip" transform="translate(186 114)">
                  <rect width="120" height="48" rx="14" />
                  <path d="M18 29c0-8 6-13 13-13 6 0 10 3 12 8 6 0 10 4 10 9H19c-1-1-1-2-1-4Z" />
                  <text x="64" y="29">CLOUD</text>
                </g>
                <g className="world-orbit-chip" transform="translate(142 409)">
                  <rect width="132" height="48" rx="14" />
                  <ellipse cx="28" cy="18" rx="11" ry="5" />
                  <path d="M17 18v13c0 3 5 5 11 5s11-2 11-5V18" fill="none" />
                  <text x="51" y="29">DATABASE</text>
                </g>
                <g className="world-orbit-chip" transform="translate(392 434)">
                  <rect width="116" height="48" rx="14" />
                  <path d="m20 18-7 6 7 6M34 18l7 6-7 6M29 14l-5 20" fill="none" />
                  <text x="55" y="29">API</text>
                </g>

                <g
                  className={`world-system-node world-system-node--stack world-interactive-zone ${activeZone === 'stack' ? 'is-selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={isPt ? 'Selecionar tecnologias e arquitetura' : 'Select technologies and architecture'}
                  onClick={() => selectZone('stack')}
                  onKeyDown={(event) => handleZoneKeyDown(event, 'stack')}
                >
                  <rect x="88" y="200" width="210" height="156" rx="28" fill="var(--world-node-surface)" stroke="#3984f4" strokeWidth="2" filter="url(#world-shadow)" />
                  <rect x="105" y="217" width="176" height="35" rx="12" fill="#2f73ee" />
                  <text x="122" y="240" className="world-svg-node-title world-svg-node-title--light">
                    {isPt ? 'STACK / ARQUITETURA' : 'STACK / ARCHITECTURE'}
                  </text>
                  <rect x="105" y="266" width="78" height="30" rx="9" className="world-svg-chip" />
                  <rect x="192" y="266" width="89" height="30" rx="9" className="world-svg-chip" />
                  <rect x="105" y="306" width="89" height="30" rx="9" className="world-svg-chip" />
                  <rect x="203" y="306" width="78" height="30" rx="9" className="world-svg-chip" />
                  <text x="144" y="286" textAnchor="middle" className="world-svg-chip-text">NODE.JS</text>
                  <text x="237" y="286" textAnchor="middle" className="world-svg-chip-text">NESTJS</text>
                  <text x="150" y="326" textAnchor="middle" className="world-svg-chip-text">REACT</text>
                  <text x="242" y="326" textAnchor="middle" className="world-svg-chip-text">DATA</text>
                  <circle cx="277" cy="235" r="5" fill="#70f0bb" filter="url(#world-glow)" />
                </g>

                <g
                  className={`world-system-node world-system-node--ai world-interactive-zone ${activeZone === 'ai' ? 'is-selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={isPt ? 'Selecionar inteligência artificial' : 'Select artificial intelligence'}
                  onClick={() => selectZone('ai')}
                  onKeyDown={(event) => handleZoneKeyDown(event, 'ai')}
                >
                  <rect x="620" y="91" width="202" height="150" rx="28" fill="var(--world-node-surface)" stroke="#8061e6" strokeWidth="2" filter="url(#world-shadow)" />
                  <rect x="638" y="109" width="166" height="37" rx="12" fill="url(#world-ai-gradient)" />
                  <text x="655" y="133" className="world-svg-node-title world-svg-node-title--light">
                    {isPt ? 'IA / AGENTES' : 'AI / AGENTS'}
                  </text>
                  <path d="M677 180 700 163l27 19 31-20 22 22M700 163l5 43M727 182l-22 24M727 182l31 21M758 162v41" className="world-neural-line" />
                  {[[677, 180], [700, 163], [705, 206], [727, 182], [758, 162], [758, 203], [780, 184]].map(([cx, cy], index) => (
                    <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={index === 3 ? 8 : 6} fill={index === 3 ? '#50dea4' : '#8f70ed'} />
                  ))}
                  <text x="721" y="228" textAnchor="middle" className="world-svg-caption">AI-KIT · CODEX · WORKFLOWS</text>
                </g>

                <g
                  className={`world-system-node world-system-node--projects world-interactive-zone ${activeZone === 'missions' ? 'is-selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={isPt ? 'Selecionar projetos' : 'Select projects'}
                  onClick={() => selectZone('missions')}
                  onKeyDown={(event) => handleZoneKeyDown(event, 'missions')}
                >
                  <rect x="625" y="348" width="205" height="136" rx="28" fill="var(--world-node-surface)" stroke="#ef963a" strokeWidth="2" filter="url(#world-shadow)" />
                  <rect x="643" y="366" width="169" height="34" rx="11" fill="url(#world-project-gradient)" />
                  <text x="660" y="389" className="world-svg-node-title world-svg-node-title--light">
                    {isPt ? 'PROJETOS' : 'PROJECTS'}
                  </text>
                  <rect x="643" y="414" width="76" height="50" rx="12" className="world-repo-card" />
                  <rect x="728" y="414" width="84" height="50" rx="12" className="world-repo-card" />
                  <path d="M659 433h32M659 444h44" className="world-repo-line" />
                  <path d="M744 433h25M744 444h50" className="world-repo-line" />
                  <circle cx="702" cy="433" r="4" fill="#50dea4" />
                  <circle cx="794" cy="433" r="4" fill="#50dea4" />
                </g>

                <g
                  className={`world-system-node world-system-node--core world-interactive-zone ${activeZone === 'profile' ? 'is-selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={isPt ? 'Selecionar perfil profissional' : 'Select professional profile'}
                  onClick={() => selectZone('profile')}
                  onKeyDown={(event) => handleZoneKeyDown(event, 'profile')}
                >
                  <rect x="337" y="185" width="218" height="194" rx="34" fill="var(--world-node-surface)" stroke="#4a8cf5" strokeWidth="3" filter="url(#world-shadow)" />
                  <rect x="354" y="202" width="184" height="102" rx="19" fill="url(#world-core-gradient)" />
                  <circle cx="373" cy="219" r="4" fill="#ff7180" />
                  <circle cx="387" cy="219" r="4" fill="#ffd064" />
                  <circle cx="401" cy="219" r="4" fill="#62efb5" />
                  <text x="446" y="270" textAnchor="middle" className="world-core-code">&lt;/&gt;</text>
                  <circle cx="522" cy="219" r="5" fill="#74ffc5" filter="url(#world-glow)" />
                  <text x="446" y="329" textAnchor="middle" className="world-core-name">ÂNGELO CASTRO</text>
                  <text x="446" y="349" textAnchor="middle" className="world-svg-caption">{isPt ? 'DEV SÊNIOR · TECH LEAD' : 'SENIOR DEV · TECH LEAD'}</text>
                </g>

                <circle className="world-data-packet world-data-packet--blue" cx="315" cy="278" r="5" />
                <circle className="world-data-packet world-data-packet--purple" cx="585" cy="188" r="5" />
                <circle className="world-data-packet world-data-packet--gold" cx="588" cy="369" r="5" />
              </svg>

            </div>
          </div>

          <aside className="world-detail-panel game-panel" aria-live="polite">
            <div className="world-detail-panel__header">
              <span className="world-detail-panel__code">{isPt ? 'ÁREA' : 'AREA'} {selectedZone.code}</span>
            </div>
            <div className="world-detail-panel__icon">{selectedZone.icon}</div>
            <span className="world-detail-panel__eyebrow">{selectedZone.eyebrow}</span>
            <h2>{selectedZone.title}</h2>
            <p>{selectedZone.description}</p>
            <div className="world-detail-panel__meta">{selectedZone.meta}</div>
            <button className="game-primary-button game-primary-button--full" onClick={() => scrollTo(selectedZone.target)}>
              {isPt ? 'Ver detalhes' : 'View details'}
              <ArrowForwardRounded fontSize="small" />
            </button>
            <div className="world-panel-hint">
              <AutoAwesomeRounded fontSize="small" />
              {isPt ? 'Selecione uma área no mapa' : 'Select an area on the map'}
            </div>
          </aside>
        </div>

        <nav className="world-mission-path" aria-label={isPt ? 'Navegação do portfólio' : 'Portfolio navigation'}>
          {[
            { id: 'about', label: isPt ? 'Perfil' : 'Profile', number: '01' },
            { id: 'skills', label: isPt ? 'Tecnologias' : 'Technologies', number: '02' },
            { id: 'projects', label: isPt ? 'Projetos' : 'Projects', number: '03' },
            { id: 'contact', label: isPt ? 'Contato' : 'Contact', number: '04' },
          ].map((step, index) => (
            <React.Fragment key={step.id}>
              <button onClick={() => scrollTo(step.id)} className={index === 0 ? 'is-active' : ''}>
                <span>{step.number}</span>
                <strong>{step.label}</strong>
              </button>
              {index < 3 && <div className="world-mission-path__line"><span /></div>}
            </React.Fragment>
          ))}
        </nav>
      </div>
    </section>
  );
};

export default memo(GameWorld);
